import type { ReactNode } from 'react'
import type { ConstellationFigure, StarCatalogue } from '@/lib/astro/catalogue'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { loadConstellations, loadStars } from '@/lib/astro/catalogue'
import { raDecToVector } from '@/lib/astro/sky'

interface FieldChartProps {
    /** Right ascension of the target in hours. */
    ra: number | null
    /** Declination of the target in degrees. */
    dec: number | null
    /** Half-width of the field in degrees. */
    field?: number
    className?: string
    label?: string
}

/** A finder chart centred on a target: the real stars around it, with the reticle on the object. */
export const FieldChart = ({ ra, dec, field = 9, className, label }: FieldChartProps): ReactNode => {
    const wrapRef = useRef<HTMLSpanElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [catalogue, setCatalogue] = useState<StarCatalogue | null>(null)
    const [figures, setFigures] = useState<ConstellationFigure[]>([])

    useEffect(() => {
        let alive = true

        loadStars().then(value => alive && setCatalogue(value)).catch(() => undefined)
        loadConstellations().then(value => alive && setFigures(value)).catch(() => undefined)

        return () => {
            alive = false
        }
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current
        const wrap = wrapRef.current
        const context = canvas?.getContext('2d')

        if (!canvas || !wrap || !context) return

        const size = Math.max(60, wrap.clientWidth)
        const ratio = Math.min(window.devicePixelRatio || 1, 2)

        canvas.width = Math.round(size * ratio)
        canvas.height = Math.round(size * ratio)
        context.setTransform(ratio, 0, 0, ratio, 0, 0)
        context.clearRect(0, 0, size, size)

        const cx = size / 2
        const cy = size / 2
        const radius = size / 2 - 3

        context.strokeStyle = 'rgba(237, 230, 216, 0.22)'
        context.lineWidth = 1
        context.beginPath()
        context.arc(cx, cy, radius, 0, Math.PI * 2)
        context.stroke()

        for (let degree = 0; degree < 360; degree += 10) {
            const angle = (degree * Math.PI) / 180
            const inner = radius - (degree % 90 === 0 ? 7 : 4)

            context.beginPath()
            context.moveTo(cx + Math.cos(angle) * inner, cy + Math.sin(angle) * inner)
            context.lineTo(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius)
            context.stroke()
        }

        if (ra === null || dec === null) return

        const c = raDecToVector(ra * 15, dec)
        const pole: [number, number, number] = [0, 0, 1]
        let ex = pole[1] * c[2] - pole[2] * c[1]
        let ey = pole[2] * c[0] - pole[0] * c[2]
        let ez = pole[0] * c[1] - pole[1] * c[0]
        const eLength = Math.hypot(ex, ey, ez) || 1

        ex /= eLength
        ey /= eLength
        ez /= eLength

        const nx = c[1] * ez - c[2] * ey
        const ny = c[2] * ex - c[0] * ez
        const nz = c[0] * ey - c[1] * ex
        const scale = radius / Math.tan((field * Math.PI) / 180)

        const project = (x: number, y: number, z: number) => {
            const depth = x * c[0] + y * c[1] + z * c[2]

            if (depth <= 0.2) return null

            const px = cx - ((x * ex + y * ey + z * ez) / depth) * scale
            const py = cy - ((x * nx + y * ny + z * nz) / depth) * scale

            if (Math.hypot(px - cx, py - cy) > radius - 2) return null

            return { x: px, y: py }
        }

        context.save()
        context.beginPath()
        context.arc(cx, cy, radius - 1, 0, Math.PI * 2)
        context.clip()

        context.strokeStyle = 'rgba(198, 167, 110, 0.32)'
        context.lineWidth = 0.8

        for (const figure of figures) {
            for (const line of figure.lines) {
                for (let i = 0; i < line.length - 1; i++) {
                    const a = line[i]
                    const b = line[i + 1]
                    const depthA = a[0] * c[0] + a[1] * c[1] + a[2] * c[2]
                    const depthB = b[0] * c[0] + b[1] * c[1] + b[2] * c[2]

                    if (depthA < 0.5 || depthB < 0.5) continue

                    const pa = { x: cx - ((a[0] * ex + a[1] * ey + a[2] * ez) / depthA) * scale, y: cy - ((a[0] * nx + a[1] * ny + a[2] * nz) / depthA) * scale }
                    const pb = { x: cx - ((b[0] * ex + b[1] * ey + b[2] * ez) / depthB) * scale, y: cy - ((b[0] * nx + b[1] * ny + b[2] * nz) / depthB) * scale }

                    context.beginPath()
                    context.moveTo(pa.x, pa.y)
                    context.lineTo(pb.x, pb.y)
                    context.stroke()
                }
            }
        }

        if (catalogue) {
            for (let i = 0; i < catalogue.count; i++) {
                const point = project(catalogue.xyz[i * 3], catalogue.xyz[i * 3 + 1], catalogue.xyz[i * 3 + 2])

                if (!point) continue

                const mag = catalogue.mag[i]

                context.fillStyle = `rgba(237, 230, 216, ${Math.min(1, 0.4 + (6.2 - mag) * 0.13)})`
                context.beginPath()
                context.arc(point.x, point.y, Math.max(0.5, (6.6 - mag) * 0.5) * (size / 160), 0, Math.PI * 2)
                context.fill()
            }
        }

        context.restore()

        context.strokeStyle = 'rgba(198, 167, 110, 0.95)'
        context.lineWidth = 1
        context.beginPath()
        context.arc(cx, cy, size * 0.06, 0, Math.PI * 2)
        context.stroke()
        context.beginPath()
        context.moveTo(cx - size * 0.16, cy)
        context.lineTo(cx - size * 0.085, cy)
        context.moveTo(cx + size * 0.085, cy)
        context.lineTo(cx + size * 0.16, cy)
        context.moveTo(cx, cy - size * 0.16)
        context.lineTo(cx, cy - size * 0.085)
        context.moveTo(cx, cy + size * 0.085)
        context.lineTo(cx, cy + size * 0.16)
        context.stroke()
    }, [catalogue, figures, ra, dec, field])

    return (
        <span ref={wrapRef} className={cn('relative block aspect-square w-full', className)}>
            <span className='media absolute inset-0 block rounded-full'>
                <canvas ref={canvasRef} role='img' aria-label={label ?? 'Finder chart'} className='block h-full w-full' />
            </span>
        </span>
    )
}
